import React, { useEffect, useState } from "react";
import { getAssignedDrawings } from "../../services/operations/drawingsheetAPI";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { Bar, Pie } from "react-chartjs-2";
import "chart.js/auto";
import { useSelector } from "react-redux";
import { 
  FileText, 
  CheckCircle, 
  Clock, 
  BarChart3, 
  PieChart, 
  Users, 
  ArrowRight, 
  TrendingUp,
  Activity,
  Target,
  Sparkles
} from "lucide-react";

const EmployeeDashboard = () => {
  const { user } = useSelector((state) => state.profile);
  const [assignedDrawings, setAssignedDrawings] = useState([]);
  const navigate = useNavigate();
  const totalTasks = assignedDrawings.length;
  const completedTasks = assignedDrawings.filter(d => d.completedBy.includes(user.id)).length;
  const pendingTasks = totalTasks - completedTasks;

  useEffect(() => {
    const fetchDrawings = async () => {
      try {
        const res = await getAssignedDrawings();
        
        setAssignedDrawings(res);
      } catch (error) {
        toast.error("Failed to fetch assigned drawings");
      }
    };
    fetchDrawings();
  }, []);

  // Chart Data for Visualization
  const chartData = {
    labels: assignedDrawings.map((drawing) => `Drawing ${drawing.drawingNumber}`),
    datasets: [
      {
        label: "Completion Percentage",
        data: assignedDrawings.map((drawing) => drawing.completedBy.includes(user.id) ? 100 : 20), 
        backgroundColor: [
          "rgba(59, 130, 246, 0.8)",
          "rgba(249, 115, 22, 0.8)", 
          "rgba(16, 185, 129, 0.8)", 
          "rgba(239, 68, 68, 0.8)",
          "rgba(139, 92, 246, 0.8)",
          "rgba(236, 72, 153, 0.8)"
        ],
        borderColor: [
          "rgb(59, 130, 246)",
          "rgb(249, 115, 22)",
          "rgb(16, 185, 129)",
          "rgb(239, 68, 68)",
          "rgb(139, 92, 246)",
          "rgb(236, 72, 153)"
        ],
        borderWidth: 2,
        borderRadius: 8,
      },
    ],
  };

  const pieData = {
    labels: ["Completed", "Pending"],
    datasets: [
      {
        data: [completedTasks, pendingTasks],
        backgroundColor: [
          "rgba(16, 185, 129, 0.8)",
          "rgba(249, 115, 22, 0.8)"
        ],
        borderColor: [
          "rgb(16, 185, 129)",
          "rgb(249, 115, 22)"
        ],
        borderWidth: 2,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          font: {
            size: 12,
            weight: 'bold'
          }
        }
      },
    },
    scales: {
      x: {
        grid: {
          display: false
        }
      },
      y: {
        grid: {
          color: 'rgba(0, 0, 0, 0.1)'
        }
      }
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Modern Header */}
        <div className="flex  lg:justify-between gap-2 mb-12">
          <div className="flex items-center gap-2  ">
            <div className="p-2  bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl shadow-xl">
              <Activity className="h-6  w-6 text-white" />
            </div>
          
            <h1 className="text-4xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
              Employee Dashboard
            </h1>
          </div>
          <p className="text-slate-600 text-lg">Track your assigned HAZOP tasks and progress</p>
        </div>

        {/* Modern Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Total Tasks Card */}
          <div className="group relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl"></div>
            <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6 transition-all duration-300 hover:shadow-xl hover:scale-105">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                    <Target className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-slate-600 uppercase tracking-wider">Total Tasks</h3>
                    <p className="text-2xl font-bold text-slate-800">{totalTasks}</p>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-blue-500" />
                <span className="text-sm text-blue-600 font-medium">Assigned to you</span>
              </div>
            </div>
          </div>

          {/* Completed Tasks Card */}
          <div className="group relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 to-green-600/10 rounded-2xl"></div>
            <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6 transition-all duration-300 hover:shadow-xl hover:scale-105">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                    <CheckCircle className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-slate-600 uppercase tracking-wider">Completed</h3>
                    <p className="text-2xl font-bold text-slate-800">{completedTasks}</p>
                  </div>
                </div>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-emerald-500 to-green-600 rounded-full transition-all duration-1000"
                  style={{ width: totalTasks > 0 ? `${(completedTasks/totalTasks)*100}%` : '0%' }}
                ></div>
              </div>
            </div>
          </div>

          {/* Pending Tasks Card */}
          <div className="group relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl"></div>
            <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6 transition-all duration-300 hover:shadow-xl hover:scale-105">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                    <Clock className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-slate-600 uppercase tracking-wider">Pending</h3>
                    <p className="text-2xl font-bold text-slate-800">{pendingTasks}</p>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-amber-500" />
                <span className="text-sm text-amber-600 font-medium">Needs attention</span>
              </div>
            </div>
          </div>
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Bar Chart */}
          <div className="relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl"></div>
            <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-slate-800">Task Progress</h2>
                  <p className="text-slate-600 text-sm mt-1">Completion percentage by drawing</p>
                </div>
                <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-lg">
                  <BarChart3 className="h-6 w-6 text-white" />
                </div>
              </div>
              <div className="h-64">
                <Bar data={chartData} options={chartOptions} />
              </div>
            </div>
          </div>

          {/* Pie Chart */}
          <div className="relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 to-green-600/10 rounded-2xl"></div>
            <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-slate-800">Task Distribution</h2>
                  <p className="text-slate-600 text-sm mt-1">Overview of task completion</p>
                </div>
                <div className="p-2 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl shadow-lg">
                  <PieChart className="h-6 w-6 text-white" />
                </div>
              </div>
              <div className="h-64">
                <Pie data={pieData} options={chartOptions} />
              </div>
            </div>
          </div>
        </div>

        {/* Assigned Drawings Table */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-500/5 to-gray-600/5 rounded-2xl"></div>
          <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 overflow-hidden">
            <div className="p-6 border-b border-white/20 bg-gradient-to-r from-slate-50/50 to-white/50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-slate-600 to-gray-700 rounded-xl shadow-lg">
                  <FileText className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-slate-800">Assigned Drawings</h2>
                  <p className="text-slate-600 text-sm mt-1">Manage your assigned HAZOP drawings</p>
                </div>
              </div>
            </div>

            {assignedDrawings.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-slate-50 to-white border-b border-white/20">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">
                        Drawing Number
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">
                        Chairman
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">
                        Scribe
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/20">
                    {assignedDrawings.map((drawing) => (
                      <tr 
                        key={drawing._id} 
                        className="hover:bg-white/50 transition-all duration-300 group"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-lg">
                              <FileText className="h-4 w-4 text-blue-600" />
                            </div>
                            <span className="text-slate-800 font-semibold">{drawing.drawingNumber}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-slate-600 font-medium">
                          {drawing.chairman.firstName} {drawing.chairman.lastName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-slate-600 font-medium">
                          {drawing.scribe.firstName} {drawing.scribe.lastName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white px-6 py-2 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2 transform hover:scale-105 font-semibold group-hover:scale-110"
                            onClick={() => navigate(`/dashboard/task-assigned`)}
                          >
                            <Users className="h-4 w-4" />
                            Create Node
                            <ArrowRight className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="p-6 bg-slate-100 rounded-2xl w-fit mx-auto mb-6">
                  <FileText className="h-16 w-16 text-slate-400 mx-auto" />
                </div>
                <p className="text-slate-500 text-xl mb-2">No assigned drawings available.</p>
                <p className="text-slate-400">Check back later for new assignments</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeDashboard;